import { useLocation, useParams } from "react-router-dom";
import Axios from "../../../utils/axios";
import '../../../assets/css/AddCatalogs.css';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Input from "../../../components/Atomos/Inputs/Input";
import RadioButton from "../../../components/Atomos/Radio/RadioButton";
import DropDown from "../../../components/Atomos/DropDown/Dropdown";
import GoBack from "../../../components/Moleculas/GoBack";
import CatalogSubTitle from "../../../components/Moleculas/Catalog Text/CatalogSubTitle";
import CatalogTitle from "../../../components/Moleculas/Catalog Text/CatalogTitle";
import { UseFirmList } from "../../../hooks/Catalogs/useFirm";
import { fetchPersonByFirm } from "../../../Services/Person";
import { IPerson } from "../../../interfaces";
import { generateRandomPassword } from "../../../utils/functions";
import ButtonsCrud from "../../../components/Catalogs/ButtonsCrud";

interface DataItem {
  id?: string;
  email: string;
  username: string;
  firm?: string;
  person?: string;
  access: string
  password?: string;
  identities?: string[];
}

interface iCatalogProps {
  mode: "add" | "edit";
}

type Errors = {
  firm: string
  person: string
  email: string
  access: string
  username: string
};
const initialErrors: Errors = {
  firm: "",
  person: "",
  email: "",
  access: "",
  username: "",
}

const AddUser: React.FC<iCatalogProps> = (props) => {
  const axios = new Axios();
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const [errors, setErrors] = useState<Errors>(initialErrors);
  const [formData, setFormData] = useState<DataItem>(state as DataItem);
  const wsgAccess = ["WSG Staff", "WSG Staff Super"];
  const [checked, setChecked] = useState(false);
  const [selectedOption, setSelectedOption] = useState(0);
  const [personData, setPersonData] = useState<IPerson[]>([]);
  const { firmData } = UseFirmList();

  const errorMap: Record<string, keyof typeof errors> = {
    Email: 'email',
    username: 'username',
  };
  
  const determineSelectedOption = () => {
      if (state?.access) {
        return wsgAccess.includes(state.access) ? 1 : 0;
      }
      if (state?.userType !== undefined) {
        return state.userType === 0 ? 0 : 1;
      }
      return 0;
    };
    
  useEffect(() => {
    setFormData(state);

    setSelectedOption(determineSelectedOption());
  }, [state]);

  const checkEditType = (index: number) => {
    const wsgFirm = firmData.find((firm: any) => {
      const labels = [firm.longName, firm.name, firm.firmName, firm.shortName];
      return labels.some(
        (label) => String(label ?? "").trim().toLowerCase() === "world services group",
      );
    });
    setSelectedOption(index);
    handleInputChange('firm', index === 0 ? '' : wsgFirm?.id);
  };

  const fetchPersonData = async (firmId: string) => {
    try {
      const data = await fetchPersonByFirm(firmId);
        if (props.mode === 'add') {
          const filteredPersonData = data.filter(
            (person: any) => !person.userId && person.firmEmail
          );
          setPersonData(filteredPersonData);
        } else {
          setPersonData(data);
        }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (formData?.firm) {
      fetchPersonData(formData.firm);
    }
    else {
      handleInputChange('person', undefined);
    }
  }, [formData?.firm])

  useEffect(() => {
    if (!formData?.person || !personData.length) {
      return
    }

    const person = personData.find((person: any) => person.id === formData?.person);
    const probableUsername = person ? person.name.slice(0, 1) + person.lastName : ""
    handleInputChange('email', person?.firmEmail);

    const username = state?.identities?.find((i: any) => i.signInType === "userName")?.issuerAssignedId || state?.username;
    handleInputChange('username', username ?? probableUsername);
  }, [formData?.person, personData])

  useEffect(() => {
    if (!checked) {
      return
    }
    validateForm();
  }, [formData]);

  useEffect(() => {
    if (!personData?.length || !state?.userId) {
      return
    }
    setPerson(state.userId);
  }, [personData])

  const setPerson = async (userId: string) => {
    try{
      const person = personData.find((person: any) => person.userId === userId);
      handleInputChange('person', person)
    } catch (error) {
      console.error(error);
    }
  }

  const validateForm = () => {
    const isFirmDisabled = selectedOption === 1 || props.mode === "edit";
    const isPersonDisabled = !formData?.firm || props.mode === "edit";
    const isEmailDisabled = true; 
    
    const newErrors:Errors = {
      firm: isFirmDisabled ? "" : (formData.firm ? "" : "Please select a firm"),
      person: isPersonDisabled ? "" : (formData.person ? "" : "Please select a person"),
      access: formData.access ? "" : "Please select an access type",
      email: isEmailDisabled ? "" : (formData.email ? 
        formData.email.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i) 
        ? "" : "Email not well formatted" :"Please enter an email"),
      username: formData.username ? "" : "Please enter a username",
    }
    setErrors(newErrors);
    setChecked(true);
    const hasErrors = Object.values(newErrors).some(error => error !== "");
    return !hasErrors;
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.Delete(`/User`, id);
      navigate(-1);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const handleSave = async () => {
    try {
      if (!validateForm()) {
        return;
      }

      const password = generateRandomPassword()
      const person = personData.find((person: any) => person.id === formData.person);
      const response = await axios.Post("/User", {
        access: formData.access,
        nickName: formData.username,
        password,
        fullName: `${person?.name ?? ''} ${person?.lastName ?? ''}`,
        email: formData.email,
        personId: person?.id,
      });

      if (response.status !== 200 ) {
        throw new Error("Error creating user")
      }
      if (state?.isContentRedirect) {
        const newState = {
          ...state.content,
          authors: [person?.id]
        }
        navigate("/Catalogs/Content/Create", { state: newState });
      } else {
        navigate(state?.redirectUrl ?? -1);
      }
    } catch (error: any) {
      Object.entries(errorMap).forEach(([keyword, field]) => {
        if (error.response?.data?.includes(keyword)) {
          setErrors(prev => ({ ...prev, [field]: `${keyword} is already registered` }));
        }
      });
      console.error(error);
    }
  };

  const handleUpdate = async () => {
    try {
      if (!validateForm()) {
        return;
      }
      const newUser = {
        id: state.userId,
        access: formData.access,
        nickName: formData.username,
        email: formData.email,
        personId: formData.person,
        identities: formData.identities,
      }
      await axios.Put(`/User`, newUser);

      navigate(-1);
    } catch (error: any) {
      Object.entries(errorMap).forEach(([keyword, field]) => {
        if (error.response?.data?.includes(keyword)) {
          setErrors(prev => ({ ...prev, [field]: `${keyword} is already registered` }));
        }
      });
      console.error(error);
    }
  };

  const handleAction = {
    saveButton: handleSave,
    updateButton: handleUpdate,
  }

  const handleButtonClick = (action: 'updateButton' | 'saveButton') => {
    handleAction[action]();
  };

  const handleInputChange = (field: keyof DataItem, value: any) => {
    setFormData((prev) => ({...prev, [field]: value?.id || value}));
  }

  const options = [
    { index: 0, text: "Firm User" },
    { index: 1, text: "WSG Staff User" },
  ];
  
  return (
    <>
        <form onSubmit={handleSubmit}>
          <CatalogTitle title={`${props.mode === "add" ? "New User" : "Edit User"}`} className='text-Default'>
            {state?.userType === 0 || state?.userType === 2 ? (
              <div>
              </div>
            ) : (
              <GoBack />
            )}
          </CatalogTitle>

          <article className="articleSection">
            <section className="formsectionNTitle">
              <CatalogSubTitle title="User Type" className=" text-accent-Default-dark" />
              <div className="formsection flex items-start w-full flex-col md:flex-row !gap-2.5 ">
                <h4 className="text-gray-font ">
                  Select User Type:
                </h4>
                <div className="flex flex-1 gap-5">
                  {options.map((option) => (
                    <RadioButton
                      key={option.index}
                      index={option.index}
                      text={option.text}
                      selected={selectedOption === option.index}
                      handleRadioButtonChange={checkEditType}
                      disabled={state?.userType === 0 || state?.userType === 2}
                    />
                  ))}
                </div>
              </div>
            </section>
            <section className="formsectionNTitle">
              <CatalogSubTitle title="User Information" className=" text-accent-Default-dark" />
              <div className=" formsection grid grid-cols-12 w-full">
                <div className=" col-span-12 md:col-span-6">
                  <DropDown
                    title={"Firm"}
                    required={selectedOption === 0 && props.mode === "add"}
                    disabled={selectedOption === 1 || props.mode === "edit"}
                    selectedObjst={firmData.find((f: any) => f.id === formData?.firm)?.name}
                    seter={(e: any) => handleInputChange('firm', e)}
                    data={firmData}
                    error={!!errors.firm}
                    errormsj={errors.firm}
                    getid={true}
                  />
                </div>
                <div className=" col-span-12 md:col-span-6">
                  <DropDown
                    title={"Person"}
                    required={!!formData?.firm && props.mode === "add"}
                    disabled={!formData?.firm || props.mode === "edit"}
                    selectedObjst={personData.find((f: any) => f.id === formData?.person)?.name}
                    seter={(e: any) => handleInputChange('person', e)}
                    data={personData}
                    error={!!errors.person}
                    errormsj={errors.person}
                    getid={true}
                    tooltip={selectedOption === 0 ? "Please select Firm" : undefined}
                  />
                </div>
                <div className="grid col-span-12 md:col-span-6 lg:col-span-4 ">
                  <Input
                    name="email"
                    value={formData?.email}
                    required={false}
                    title="Email"
                    error={!!errors.email}
                    errorMsj={errors.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    tooltip="Email is set in Person Catalog."
                    disabled
                  />
                </div>
                <div className="grid col-span-12 md:col-span-6 lg:col-span-4">
                  <DropDown
                    title={"Access Type"}
                    required={true}
                    selectedObjst={formData?.access}
                    seter={(e: any) => handleInputChange('access', e)}
                    data={selectedOption === 0
                      ? [{ id: "member", name: "Member" }, { id: "dataAdmin", name: "Data Admin + Member" }]
                      : [{ id: "wsgStaff", name: "WSG Staff" }, { id: 'wsgSuper', name: "WSG Staff Super" }]}
                    error={!!errors.access}
                    errormsj={errors.access}
                  disabled={props.mode === "edit" && state?.user?.access === 'WSG Staff'}
                  />
                </div>
                <div className="grid col-span-12 md:col-span-6 lg:col-span-4">
                  <Input
                    name="user"
                    value={formData?.username}
                    required={true}
                    title="Username"
                    error={!!errors.username}
                    errorMsj={errors.username}
                    onChange={e => handleInputChange('username', e.target.value.replace(/\s+/g, ""))}
                  />
                </div>
              </div>
            </section>
            <ButtonsCrud
              Catalog="User"
              mode={props.mode}
              id={id ? id : ""}
              disabled={Object.values(errors || {}).some((error) => error !== "")} 
              actionButton={handleButtonClick}
              onDelete={() => handleDelete(state.userId)}
              redirectUrl={state?.userType === 0 || state?.userType === 2 ? "/Catalogs/Person/" : undefined}
              state={state}
              newState={formData}
              customFieldNames={{
                "access": "Access Type",
              }}
            />
          </article>
        </form>
    </>
  );
};

export default AddUser;
